import { forwardRef, type InputHTMLAttributes, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

/* SDR cp-input: 9px radius, line border, accent border + 3px accent-wash
   ring on focus. Form inputs stay readable (12.5px floor per the brief). */
const baseField =
  "block w-full rounded-[9px] border border-[var(--cp-line)] bg-[var(--cp-surface)] px-3 py-2 text-[12.5px] text-[var(--cp-ink)] placeholder:text-[var(--cp-ink-3)] transition-colors focus:border-[var(--cp-accent)] focus:outline-none focus:ring-[3px] focus:ring-[var(--cp-accent-wash)] disabled:cursor-not-allowed disabled:bg-[var(--cp-surface-2)]";

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return <input ref={ref} className={cn(baseField, className)} {...rest} />;
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...rest }, ref) {
    return <textarea ref={ref} className={cn(baseField, "min-h-[88px] resize-y", className)} {...rest} />;
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, ...rest }, ref) {
    return <select ref={ref} className={cn(baseField, "pr-8", className)} {...rest} />;
  },
);

export function Label({
  children,
  htmlFor,
  required,
  hint,
  className,
}: {
  children: React.ReactNode;
  htmlFor?: string;
  required?: boolean;
  hint?: string;
  className?: string;
}) {
  return (
    <label htmlFor={htmlFor} className={cn("mb-1.5 flex items-center justify-between text-xs font-medium text-[var(--cp-ink-2)]", className)}>
      <span>
        {children}
        {required && <span className="ml-0.5 text-[var(--cp-rose-ink)]">*</span>}
      </span>
      {hint && <span className="font-normal text-[var(--cp-ink-3)]">{hint}</span>}
    </label>
  );
}

export function Field({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn("flex flex-col", className)}>{children}</div>;
}
